import BASE_URL from "./baseurl";

const AUTH_USER_KEY = "auth_user";

const buildUrl = (path) => `${BASE_URL}${path}`;

export const buildAbsoluteFileUrl = (path) => {
  const normalized = String(path || "").trim();
  if (!normalized) {
    return "";
  }
  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    return normalized;
  }
  try {
    return new URL(normalized, BASE_URL).toString();
  } catch (error) {
    return normalized;
  }
};

const parseJsonSafe = async (response) => {
  try {
    return await response.clone().json();
  } catch (error) {
    return null;
  }
};

class ApiError extends Error {
  constructor(message, { status = 0, details = null, isNetworkError = false } = {}) {
    super(message || "Request failed.");
    this.name = "ApiError";
    this.status = Number(status) || 0;
    this.details = details;
    this.isNetworkError = Boolean(isNetworkError);
  }
}

const request = async (path, options = {}) => {
  const isFormDataBody = options.body instanceof FormData;
  const nextHeaders = {
    ...(options.headers || {}),
  };
  if (!isFormDataBody && !nextHeaders["Content-Type"]) {
    nextHeaders["Content-Type"] = "application/json";
  }

  let response;
  try {
    response = await fetch(buildUrl(path), {
      credentials: "include",
      headers: nextHeaders,
      ...options,
    });
  } catch (error) {
    throw new ApiError("Network error.", {
      status: 0,
      details: null,
      isNetworkError: true,
    });
  }

  const payload = await parseJsonSafe(response);

  if (!response.ok || payload?.ok === false) {
    const fallbackText = (await response.text()).trim();
    const message = payload?.error
      || payload?.message
      || fallbackText
      || "Request failed.";
    const details = payload?.details;
    if (details && typeof details === "object") {
      const detailsText = Object.entries(details)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : String(value)}`)
        .join(" | ");
      throw new ApiError(detailsText ? `${message} (${detailsText})` : message, {
        status: response.status,
        details,
      });
    }
    throw new ApiError(message, {
      status: response.status,
      details,
    });
  }

  return payload?.data ?? payload;
};

const requestWithAccessToken = async (path, options = {}) => {
  return request(path, options);
};

export const authStorage = {
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "null") || null;
    } catch (error) {
      return null;
    }
  },
  getUserId: () => String(authStorage.getUser()?.id || ""),
  // Backward-compatible helper for legacy callers.
  getAccessToken: () => String(authStorage.getUser()?.id || ""),
  // Refresh tokens are removed; keep empty string for legacy checks.
  getRefreshToken: () => "",
  setSession: ({ user }) => {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user || {}));
  },
  setTokenPair: (data) => authStorage.setSession(data),
  clear: () => {
    localStorage.removeItem(AUTH_USER_KEY);
  },
};

export const authApi = {
  login: async ({ username, password }) => {
    const data = await request("/users/auth/login/", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });

    authStorage.setSession(data);
    return data;
  },

  logout: async () => {
    await request("/users/auth/logout/", {
      method: "POST",
    });
    authStorage.clear();
  },

  me: async () => {
    return requestWithAccessToken("/users/auth/me/", { method: "GET" });
  },

  meWithAutoRefresh: async () => {
    return authApi.me();
  },
};

export const usersApi = {
  list: async () => {
    return requestWithAccessToken("/users/", { method: "GET" });
  },

  create: async (payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        return;
      }
      if (key === "profile_image_file") {
        formData.append("profile_image", value);
        return;
      }
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }
      formData.append(key, String(value));
    });

    return requestWithAccessToken("/users/", {
      method: "POST",
      body: formData,
    });
  },

  update: async (userId, payload) => {
    const normalizedPayload = payload || {};
    const hasProfileImageFile = normalizedPayload.profile_image_file instanceof File;

    if (!hasProfileImageFile) {
      const jsonPayload = {};
      Object.entries(normalizedPayload).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          return;
        }
        if (key === "profile_image_file") {
          return;
        }
        jsonPayload[key] = value;
      });
      return requestWithAccessToken(`/users/${userId}/`, {
        method: "PATCH",
        body: JSON.stringify(jsonPayload),
      });
    }

    const formData = new FormData();
    Object.entries(normalizedPayload).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        return;
      }
      if (key === "profile_image_file") {
        formData.append("profile_image", value);
        return;
      }
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }
      formData.append(key, String(value));
    });

    return requestWithAccessToken(`/users/${userId}/`, {
      method: "PATCH",
      body: formData,
    });
  },

  bulkDelete: async (userIds) => {
    return requestWithAccessToken("/users/bulk-delete/", {
      method: "POST",
      body: JSON.stringify({ user_ids: userIds }),
    });
  },

  activityLogs: async () => {
    return requestWithAccessToken("/users/activity-logs/", { method: "GET" });
  },

  loginAttempts: async () => {
    return requestWithAccessToken("/users/login-attempts/", { method: "GET" });
  },
};

export const projectsApi = {
  list: async () => {
    try {
      return await requestWithAccessToken("/projects/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/projects/", { method: "GET" });
    }
  },

  create: async (payload) => {
    const body = payload instanceof FormData ? payload : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken("/projects/", {
        method: "POST",
        body,
      });
    } catch (error) {
      return requestWithAccessToken("/projects/", {
        method: "POST",
        body,
      });
    }
  },

  update: async (projectId, payload) => {
    const body = payload instanceof FormData ? payload : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken(`/projects/${projectId}/`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      return requestWithAccessToken(`/projects/${projectId}/`, {
        method: "PATCH",
        body,
      });
    }
  },

  bulkDelete: async (projectIds) => {
    try {
      return await requestWithAccessToken("/projects/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ project_ids: projectIds }),
      });
    } catch (error) {
      return requestWithAccessToken("/projects/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ project_ids: projectIds }),
      });
    }
  },
};

export const sourcesApi = {
  listReceive: async () => {
    try {
      return await requestWithAccessToken("/sources/receive/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/sources/receive/", { method: "GET" });
    }
  },

  createReceive: async ({
    name,
    platform,
    link,
    price_template_id,
    receive_source_type,
    receive_interval_seconds,
    rubika_account_id,
    eita_account_id,
    anti_ad_with_ai,
    anti_ad_with_system,
    anti_ad_with_ai_prompt,
    anti_ad_with_system_rules,
  }) => {
    const normalizedPlatform = String(platform || "").trim().toLowerCase();
    const isEitaPlatform = normalizedPlatform === "eita" || normalizedPlatform === "eitaa" || normalizedPlatform === "ایتا";
    const isEitaWithVideoPlatform = normalizedPlatform === "eita with video" || normalizedPlatform === "eita_with_video";
    const isPricePlatform = normalizedPlatform === "price" || normalizedPlatform === "دریافت قیمت";
    const payload = {
      name,
      platform,
      link: isPricePlatform ? "" : link,
      price_template_id: isPricePlatform ? (price_template_id || null) : null,
      receive_interval_seconds: Number.parseInt(String(receive_interval_seconds || (isPricePlatform ? 3000 : 60)), 10) || (isPricePlatform ? 3000 : 60),
      receive_source_type: isEitaPlatform || isEitaWithVideoPlatform || isPricePlatform
        ? "dynamic"
        : (String(receive_source_type || "dynamic").trim().toLowerCase() === "static"
        ? "static"
        : "dynamic"),
      rubika_account_id: isPricePlatform ? null : (rubika_account_id || null),
      eita_account_id: isPricePlatform ? null : (eita_account_id || null),
      anti_ad_with_ai: isPricePlatform ? false : Boolean(anti_ad_with_ai),
      anti_ad_with_system: isPricePlatform ? false : Boolean(anti_ad_with_system),
      anti_ad_with_ai_prompt: isPricePlatform ? "" : String(anti_ad_with_ai_prompt || ""),
      anti_ad_with_system_rules: isPricePlatform ? [] : (Array.isArray(anti_ad_with_system_rules) ? anti_ad_with_system_rules : []),
    };
    try {
      return await requestWithAccessToken("/sources/receive/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      return requestWithAccessToken("/sources/receive/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    }
  },

  updateReceive: async (sourceId, payload) => {
    const normalizedPlatform = String(payload?.platform || "").trim().toLowerCase();
    const isEitaPlatform = normalizedPlatform === "eita" || normalizedPlatform === "eitaa" || normalizedPlatform === "ایتا";
    const isEitaWithVideoPlatform = normalizedPlatform === "eita with video" || normalizedPlatform === "eita_with_video";
    const isPricePlatform = normalizedPlatform === "price" || normalizedPlatform === "دریافت قیمت";
    const nextPayload = {
      ...(payload || {}),
      link: isPricePlatform ? "" : (payload?.link || ""),
      rubika_account_id: isPricePlatform ? null : (payload?.rubika_account_id || null),
      eita_account_id: isPricePlatform ? null : (payload?.eita_account_id || null),
      anti_ad_with_ai: isPricePlatform ? false : Boolean(payload?.anti_ad_with_ai),
      anti_ad_with_system: isPricePlatform ? false : Boolean(payload?.anti_ad_with_system),
      anti_ad_with_ai_prompt: isPricePlatform ? "" : String(payload?.anti_ad_with_ai_prompt || ""),
      anti_ad_with_system_rules: isPricePlatform ? [] : (Array.isArray(payload?.anti_ad_with_system_rules) ? payload.anti_ad_with_system_rules : []),
      receive_source_type: isEitaPlatform || isEitaWithVideoPlatform || isPricePlatform
        ? "dynamic"
        : (String(payload?.receive_source_type || "dynamic").trim().toLowerCase() === "static"
        ? "static"
        : "dynamic"),
    };
    try {
      return await requestWithAccessToken(`/sources/receive/${sourceId}/`, {
        method: "PATCH",
        body: JSON.stringify(nextPayload),
      });
    } catch (error) {
      return requestWithAccessToken(`/sources/receive/${sourceId}/`, {
        method: "PATCH",
        body: JSON.stringify(nextPayload),
      });
    }
  },

  bulkDeleteReceive: async (sourceIds) => {
    try {
      return await requestWithAccessToken("/sources/receive/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ source_ids: sourceIds }),
      });
    } catch (error) {
      return requestWithAccessToken("/sources/receive/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ source_ids: sourceIds }),
      });
    }
  },

  listPublish: async () => {
    try {
      return await requestWithAccessToken("/sources/publish/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/sources/publish/", { method: "GET" });
    }
  },

  createPublish: async ({ name, platform, link }) => {
    try {
      return await requestWithAccessToken("/sources/publish/", {
        method: "POST",
        body: JSON.stringify({ name, platform, link }),
      });
    } catch (error) {
      return requestWithAccessToken("/sources/publish/", {
        method: "POST",
        body: JSON.stringify({ name, platform, link }),
      });
    }
  },

  updatePublish: async (sourceId, payload) => {
    try {
      return await requestWithAccessToken(`/sources/publish/${sourceId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    } catch (error) {
      return requestWithAccessToken(`/sources/publish/${sourceId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    }
  },

  bulkDeletePublish: async (sourceIds) => {
    try {
      return await requestWithAccessToken("/sources/publish/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ source_ids: sourceIds }),
      });
    } catch (error) {
      return requestWithAccessToken("/sources/publish/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ source_ids: sourceIds }),
      });
    }
  },
};

export const processApi = {
  listProjectQueueItems: async ({ projectId }) => {
    const query = new URLSearchParams();
    query.set("project_id", String(projectId || ""));
    const path = `/process/project-queues/?${query.toString()}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },
  restart: async ({ cleanup_mode = "tasks_only" } = {}) => {
    try {
      return await requestWithAccessToken("/process/restart/", {
        method: "POST",
        body: JSON.stringify({ cleanup_mode }),
      });
    } catch (error) {
      return requestWithAccessToken("/process/restart/", {
        method: "POST",
        body: JSON.stringify({ cleanup_mode }),
      });
    }
  },
};

export const newsApi = {
  list: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || String(value).trim() === "") {
        return;
      }
      query.set(key, String(value));
    });
    const path = `/news/${query.toString() ? `?${query.toString()}` : ""}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },

  detail: async (newsId) => {
    try {
      return await requestWithAccessToken(`/news/${newsId}/`, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(`/news/${newsId}/`, { method: "GET" });
    }
  },

  create: async (payload) => {
    const hasAnyFile = (
      payload?.make_media_file instanceof File
      || payload?.watermark_news_file instanceof File
    );
    const body = hasAnyFile
      ? (() => {
          const formData = new FormData();
          Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") {
              return;
            }
            if (key === "scheduled_publish_timing" && value && typeof value === "object") {
              formData.append(key, JSON.stringify(value));
              return;
            }
            formData.append(key, value);
          });
          return formData;
        })()
      : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken("/news/", {
        method: "POST",
        body,
      });
    } catch (error) {
      return requestWithAccessToken("/news/", {
        method: "POST",
        body,
      });
    }
  },

  update: async (newsId, payload) => {
    const hasAnyFile = (
      payload?.make_media_file instanceof File
      || payload?.watermark_news_file instanceof File
    );
    const body = hasAnyFile
      ? (() => {
          const formData = new FormData();
          Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") {
              return;
            }
            if (key === "scheduled_publish_timing" && value && typeof value === "object") {
              formData.append(key, JSON.stringify(value));
              return;
            }
            formData.append(key, value);
          });
          return formData;
        })()
      : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken(`/news/${newsId}/`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      return requestWithAccessToken(`/news/${newsId}/`, {
        method: "PATCH",
        body,
      });
    }
  },

  bulkDelete: async (newsIds) => {
    try {
      return await requestWithAccessToken("/news/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ news_ids: newsIds }),
      });
    } catch (error) {
      return requestWithAccessToken("/news/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ news_ids: newsIds }),
      });
    }
  },
};

export const adsApi = {
  list: async () => {
    try {
      return await requestWithAccessToken("/ads/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/ads/", { method: "GET" });
    }
  },

  detail: async (adId) => {
    try {
      return await requestWithAccessToken(`/ads/${adId}/`, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(`/ads/${adId}/`, { method: "GET" });
    }
  },

  create: async (payload) => {
    const hasMediaFile = (
      payload?.make_media_file instanceof File
      || payload?.watermark_ad_file instanceof File
    );
    const body = hasMediaFile
      ? (() => {
          const formData = new FormData();
          Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") {
              return;
            }
            if (key === "scheduled_publish_rows" && Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
              return;
            }
            formData.append(key, value);
          });
          return formData;
        })()
      : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken("/ads/", {
        method: "POST",
        body,
      });
    } catch (error) {
      return requestWithAccessToken("/ads/", {
        method: "POST",
        body,
      });
    }
  },

  update: async (adId, payload) => {
    const hasMediaFile = (
      payload?.make_media_file instanceof File
      || payload?.watermark_ad_file instanceof File
    );
    const body = hasMediaFile
      ? (() => {
          const formData = new FormData();
          Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") {
              return;
            }
            if (key === "scheduled_publish_rows" && Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
              return;
            }
            formData.append(key, value);
          });
          return formData;
        })()
      : JSON.stringify(payload || {});
    try {
      return await requestWithAccessToken(`/ads/${adId}/`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      return requestWithAccessToken(`/ads/${adId}/`, {
        method: "PATCH",
        body,
      });
    }
  },

  bulkDelete: async (adIds) => {
    try {
      return await requestWithAccessToken("/ads/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ ad_ids: adIds }),
      });
    } catch (error) {
      return requestWithAccessToken("/ads/bulk-delete/", {
        method: "POST",
        body: JSON.stringify({ ad_ids: adIds }),
      });
    }
  },
};

export const dashboardApi = {
  stats: async () => {
    try {
      return await requestWithAccessToken("/dashboard/stats/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/dashboard/stats/", { method: "GET" });
    }
  },

  systemUsage: async () => {
    try {
      return await requestWithAccessToken("/dashboard/system-usage/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/dashboard/system-usage/", { method: "GET" });
    }
  },

  newsTrend: async ({ metric, period, projectId }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("metric", String(metric || ""));
    searchParams.set("period", String(period || ""));
    if (projectId && String(projectId) !== "all") {
      searchParams.set("project_id", String(projectId));
    }
    const query = searchParams.toString();
    const path = `/dashboard/news-trend/${query ? `?${query}` : ""}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },
};

export const reportsApi = {
  topProjectsPublished: async ({ period, projectId }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("period", String(period || "monthly"));
    if (projectId && String(projectId).trim() && String(projectId) !== "all") {
      searchParams.set("project_id", String(projectId));
    }
    const query = searchParams.toString();
    const path = `/reports/top-projects-published/${query ? `?${query}` : ""}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },

  topReceiveSources: async ({ period, projectId }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("period", String(period || "monthly"));
    if (projectId && String(projectId).trim() && String(projectId) !== "all") {
      searchParams.set("project_id", String(projectId));
    }
    const query = searchParams.toString();
    const path = `/reports/top-receive-sources/${query ? `?${query}` : ""}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },

  systemEvents: async ({ rangeMinutes }) => {
    const searchParams = new URLSearchParams();
    if (rangeMinutes && String(rangeMinutes).trim()) {
      searchParams.set("range_minutes", String(rangeMinutes).trim());
    }
    const query = searchParams.toString();
    const path = `/reports/system-events/${query ? `?${query}` : ""}`;
    try {
      return await requestWithAccessToken(path, { method: "GET" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "GET" });
    }
  },

  exportSystemEvents: async ({ rangeMinutes, format }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("format", String(format || "xlsx"));
    if (rangeMinutes && String(rangeMinutes).trim()) {
      searchParams.set("range_minutes", String(rangeMinutes).trim());
    }
    const query = searchParams.toString();
    const path = `/reports/system-events/export/${query ? `?${query}` : ""}`;

    const fetchBlobWithToken = async () => {
      const response = await fetch(buildUrl(path), {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        const payload = await parseJsonSafe(response);
        throw new Error(payload?.error || payload?.message || "Export failed.");
      }
      return response.blob();
    };

    try {
      return await fetchBlobWithToken();
    } catch (error) {
      return fetchBlobWithToken();
    }
  },
};

export const settingsApi = {

  listRubikaAccounts: async () => {
    try {
      return await requestWithAccessToken("/settings/rubika/accounts/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/rubika/accounts/", { method: "GET" });
    }
  },
  reactivateRubikaAccount: async ({ accountId }) => {
    const path = `/settings/rubika/accounts/${accountId}/reactivate/`;
    try {
      return await requestWithAccessToken(path, { method: "POST" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "POST" });
    }
  },

  sendRubikaCode: async ({ phone }) => {
    try {
      return await requestWithAccessToken("/settings/rubika/send-code/", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/rubika/send-code/", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
    }
  },

  verifyRubikaCode: async ({ phone, code }) => {
    try {
      return await requestWithAccessToken("/settings/rubika/verify-code/", {
        method: "POST",
        body: JSON.stringify({ phone, code }),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/rubika/verify-code/", {
        method: "POST",
        body: JSON.stringify({ phone, code }),
      });
    }
  },

  listEitaAccounts: async () => {
    try {
      return await requestWithAccessToken("/settings/eita/accounts/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/eita/accounts/", { method: "GET" });
    }
  },
  reactivateEitaAccount: async ({ accountId }) => {
    const path = `/settings/eita/accounts/${accountId}/reactivate/`;
    try {
      return await requestWithAccessToken(path, { method: "POST" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "POST" });
    }
  },

  sendEitaCode: async ({ phone }) => {
    try {
      return await requestWithAccessToken("/settings/eita/send-code/", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/eita/send-code/", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
    }
  },

  verifyEitaCode: async ({ phone, code, firstName = "", lastName = "" }) => {
    const body = {
      phone,
      code,
      first_name: firstName,
      last_name: lastName,
    };
    try {
      return await requestWithAccessToken("/settings/eita/verify-code/", {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/eita/verify-code/", {
        method: "POST",
        body: JSON.stringify(body),
      });
    }
  },
  listRobots: async () => {
    try {
      return await requestWithAccessToken("/settings/robots/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/robots/", { method: "GET" });
    }
  },

  getGeneralConfig: async () => {
    try {
      return await requestWithAccessToken("/settings/general-config/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/general-config/", { method: "GET" });
    }
  },

  updateGeneralConfig: async (payload) => {
    try {
      return await requestWithAccessToken("/settings/general-config/", {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/general-config/", {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    }
  },

  getBackupConfig: async () => {
    try {
      return await requestWithAccessToken("/settings/backup-config/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/backup-config/", { method: "GET" });
    }
  },

  updateBackupConfig: async ({ backup_duration }) => {
    try {
      return await requestWithAccessToken("/settings/backup-config/", {
        method: "PATCH",
        body: JSON.stringify({ backup_duration }),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/backup-config/", {
        method: "PATCH",
        body: JSON.stringify({ backup_duration }),
      });
    }
  },

  listBackups: async () => {
    try {
      return await requestWithAccessToken("/settings/backups/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/backups/", { method: "GET" });
    }
  },

  createBackup: async () => {
    try {
      return await requestWithAccessToken("/settings/backups/create/", { method: "POST" });
    } catch (error) {
      return requestWithAccessToken("/settings/backups/create/", { method: "POST" });
    }
  },

  downloadBackup: async ({ backupCode }) => {
    const path = `/settings/backups/${backupCode}/download/`;
    const fetchBlobWithToken = async () => {
      const response = await fetch(buildUrl(path), {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        const payload = await parseJsonSafe(response);
        throw new Error(payload?.error || payload?.message || "Backup download failed.");
      }
      const contentDisposition = String(response.headers.get("content-disposition") || "");
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = fileNameMatch?.[1] || "";
      const blob = await response.blob();
      return { blob, filename };
    };
    try {
      return await fetchBlobWithToken();
    } catch (error) {
      return fetchBlobWithToken();
    }
  },

  restoreBackup: async ({ backupCode }) => {
    const path = `/settings/backups/${backupCode}/restore/`;
    return requestWithAccessToken(path, { method: "POST" });
  },

  listWhitelistIps: async () => {
    try {
      return await requestWithAccessToken("/settings/whitelist-ips/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/whitelist-ips/", { method: "GET" });
    }
  },

  listBlockedPublishSources: async () => {
    try {
      return await requestWithAccessToken("/settings/blocked-publish-sources/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/blocked-publish-sources/", { method: "GET" });
    }
  },

  createWhitelistIp: async ({ ip, user_id }) => {
    try {
      return await requestWithAccessToken("/settings/whitelist-ips/", {
        method: "POST",
        body: JSON.stringify({ ip, user_id }),
      });
    } catch (error) {
      return requestWithAccessToken("/settings/whitelist-ips/", {
        method: "POST",
        body: JSON.stringify({ ip, user_id }),
      });
    }
  },

  deleteWhitelistIp: async ({ whitelist_ip_id }) => {
    const path = `/settings/whitelist-ips/${whitelist_ip_id}/`;
    try {
      return await requestWithAccessToken(path, { method: "DELETE" });
    } catch (error) {
      return requestWithAccessToken(path, { method: "DELETE" });
    }
  },

  getHomepageConfig: async () => {
    try {
      return await requestWithAccessToken("/settings/homepage-config/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/settings/homepage-config/", { method: "GET" });
    }
  },

  updateHomepageConfig: async (payload) => {
    const normalized = payload || {};
    const hasLogoFile = normalized.home_logo instanceof File;

    if (!hasLogoFile) {
      const jsonPayload = {
        project_title: String(normalized.project_title ?? ""),
        home_description: String(normalized.home_description ?? ""),
        terms_and_conditions: String(normalized.terms_and_conditions ?? ""),
      };
      try {
        return await requestWithAccessToken("/settings/homepage-config/", {
          method: "PATCH",
          body: JSON.stringify(jsonPayload),
        });
      } catch (error) {
        return requestWithAccessToken("/settings/homepage-config/", {
          method: "PATCH",
          body: JSON.stringify(jsonPayload),
        });
      }
    }

    const formData = new FormData();
    formData.append("project_title", String(normalized.project_title ?? ""));
    formData.append("home_description", String(normalized.home_description ?? ""));
    formData.append("terms_and_conditions", String(normalized.terms_and_conditions ?? ""));
    formData.append("home_logo", normalized.home_logo);

    try {
      return await requestWithAccessToken("/settings/homepage-config/", {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      return requestWithAccessToken("/settings/homepage-config/", {
        method: "POST",
        body: formData,
      });
    }
  },
};

export const priceTemplatesApi = {
  list: async () => {
    try {
      return await requestWithAccessToken("/prices/templates/", { method: "GET" });
    } catch (error) {
      return requestWithAccessToken("/prices/templates/", { method: "GET" });
    }
  },

  create: async (payload) => {
    try {
      return await requestWithAccessToken("/prices/templates/", {
        method: "POST",
        body: JSON.stringify(payload || {}),
      });
    } catch (error) {
      return requestWithAccessToken("/prices/templates/", {
        method: "POST",
        body: JSON.stringify(payload || {}),
      });
    }
  },

  update: async (templateId, payload) => {
    try {
      return await requestWithAccessToken(`/prices/templates/${templateId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    } catch (error) {
      return requestWithAccessToken(`/prices/templates/${templateId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload || {}),
      });
    }
  },

  delete: async (templateId) => {
    try {
      return await requestWithAccessToken(`/prices/templates/${templateId}/`, { method: "DELETE" });
    } catch (error) {
      return requestWithAccessToken(`/prices/templates/${templateId}/`, { method: "DELETE" });
    }
  },
};

export const publicContentApi = {
  getHomepageConfig: async () => request("/settings/homepage-config/public/", { method: "GET" }),
};
